import { useEffect, useState, useCallback } from 'react';
import mqttService from '../services/mqttService';

/**
 * Custom hook for MQTT functionality
 * Provides connection status and message handling
 */
export const useMQTT = () => {
  const [connectionStatus, setConnectionStatus] = useState(mqttService.getConnectionStatus());
  const [messages, setMessages] = useState([]);

  // Update connection status
  const updateConnectionStatus = useCallback(() => {
    setConnectionStatus(mqttService.getConnectionStatus());
  }, []);

  // Handle MQTT messages
  const handleMQTTMessage = useCallback((event) => {
    const { topic, message, timestamp } = event.detail;
    
    setMessages(prevMessages => {
      const newMessage = {
        id: `${topic}-${timestamp}-${Math.random()}`,
        topic,
        message,
        timestamp,
        receivedAt: new Date().toISOString()
      };
      
      // Keep only last 100 messages to prevent memory issues
      return [newMessage, ...prevMessages.slice(0, 99)];
    });
  }, []);

  // Connect to MQTT
  const connect = useCallback(async () => {
    try {
      await mqttService.connect();
      updateConnectionStatus();
    } catch (error) {
      console.error('[useMQTT] Connection error:', error);
    }
  }, [updateConnectionStatus]);

  // Disconnect from MQTT
  const disconnect = useCallback(() => {
    mqttService.disconnect();
    updateConnectionStatus();
  }, [updateConnectionStatus]);

  // Publish message
  const publish = useCallback((topic, message, options = {}) => {
    return mqttService.publish(topic, message, options);
  }, []);

  // Subscribe to topic
  const subscribe = useCallback((topic, callback) => {
    return mqttService.subscribe(topic, callback);
  }, []);

  // Unsubscribe from topic
  const unsubscribe = useCallback((topic) => {
    return mqttService.unsubscribe(topic);
  }, []);

  // Clear messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  useEffect(() => {
    // Listen for MQTT messages
    window.addEventListener('mqttMessage', handleMQTTMessage);

    // Update connection status periodically
    const statusInterval = setInterval(updateConnectionStatus, 1000);

    return () => {
      window.removeEventListener('mqttMessage', handleMQTTMessage);
      clearInterval(statusInterval);
    };
  }, [handleMQTTMessage, updateConnectionStatus]);

  return {
    // Connection methods
    connect,
    disconnect,
    
    // Message methods
    publish,
    subscribe,
    unsubscribe,
    
    // State
    connectionStatus,
    messages,
    clearMessages,
    
    // Computed values
    isConnected: connectionStatus.isConnected,
    isConnecting: connectionStatus.isConnecting,
    reconnectAttempts: connectionStatus.reconnectAttempts,
    clientId: connectionStatus.clientId,
    subscribedTopics: connectionStatus.subscribedTopics
  };
};

export default useMQTT;